<template>
  <div class="portfolio-justified" ref="root">
    <div id="portfolio-gallery">
      <div
        v-for="(row, rIndex) in rows"
        :key="rIndex"
        class="jg-row"
        :style="{ height: row.height + 'px', gap: gutter + 'px', marginBottom: gutter + 'px' }"
      >
        <a
          v-for="item in row.items"
          :key="item.src"
          class="jg-item"
          :href="item.src"
          :data-pswp-src="item.src"
          :data-pswp-width="item.w"
          :data-pswp-height="item.h"
          :title="item.caption"
          :style="{ width: item.boxWidth + 'px', height: row.height + 'px' }"
        >
          <img
            :src="item.src"
            :alt="item.caption || ''"
            :width="Math.round(item.boxWidth)"
            :height="Math.round(row.height)"
            loading="lazy"
            decoding="async"
          />
          <span v-if="showCaptions && item.caption" class="jg-caption">{{ item.caption }}</span>
        </a>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onBeforeUnmount } from 'vue'
import PhotoSwipeLightbox from 'photoswipe/lightbox'
import 'photoswipe/style.css'

/** @typedef {{ src: string, caption?: string, w?: number, h?: number }} PhotoItem */

const props = defineProps({
  items: { type: Array, required: true }, // Array<PhotoItem>
  targetRowHeight: { type: Number, default: 280 },
  gutter: { type: Number, default: 10 },
  maxScale: { type: Number, default: 1.2 },
  lastRow: { type: String, default: 'left' }, // 'justify' | 'left'
  showCaptions: { type: Boolean, default: false },
})

const root = ref(null)
const containerWidth = ref(0)

let ro = null
let lightbox = null

onMounted(() => {
  const update = () => {
    if (root.value) containerWidth.value = Math.floor(root.value.clientWidth)
  }
  update()
  ro = new ResizeObserver(update)
  if (root.value) ro.observe(root.value)

  // Init PhotoSwipe lightbox
  lightbox = new PhotoSwipeLightbox({
    gallery: '#portfolio-gallery',
    children: 'a',
    pswpModule: () => import('photoswipe'),
  })
  lightbox.init()
})

onBeforeUnmount(() => {
  if (ro && root.value) ro.unobserve(root.value)
  ro = null
  if (lightbox) {
    lightbox.destroy()
    lightbox = null
  }
})

const gutter = computed(() => props.gutter)

const rows = computed(() => {
  const W = containerWidth.value
  if (!W || !props.items || !props.items.length) return []

  const items = props.items.map((i) => {
    const w = i.w ?? 3 // fallback ratio 3:2
    const h = i.h ?? 2
    const ar = w / h
    return { ...i, w, h, ar }
  })

  const out = []
  let row = []
  let aspectSum = 0

  const pushRow = (justify) => {
    const gap = gutter.value
    const count = row.length
    const available = W - gap * (count - 1)
    let height = justify ? available / aspectSum : props.targetRowHeight

    if (justify && height > props.targetRowHeight * props.maxScale) {
      height = props.targetRowHeight * props.maxScale
    }

    const items = row.map((it) => ({ ...it, boxWidth: it.ar * height }))
    out.push({ height, items })

    row = []
    aspectSum = 0
  }

  for (const it of items) {
    row.push(it)
    aspectSum += it.ar

    const gap = gutter.value
    const projectedWidth = aspectSum * props.targetRowHeight + gap * (row.length - 1)
    if (projectedWidth >= W) pushRow(true)
  }

  if (row.length) pushRow(props.lastRow === 'justify')

  return out
})
</script>

<style scoped>
.portfolio-justified {
  width: 100%;
}

.jg-row {
  display: flex;
}

.jg-item {
  position: relative;
  display: block;
  overflow: hidden;
  border-radius: 6px;
}

.jg-item img {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.2s ease-out;
}

.jg-item:hover img {
  transform: scale(1.03);
}

.jg-caption {
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  padding: 6px 10px;
  font-size: 13px;
  color: #fff;
  background: linear-gradient(to top, rgba(0, 0, 0, 0.65), rgba(0, 0, 0, 0));
  opacity: 0;
  transition: opacity 0.2s ease;
  pointer-events: none;
}

.jg-item:hover .jg-caption {
  opacity: 1;
}
</style>
